<template>
  <div class="wrapper flex column">
    <div
      v-if="accessDenied2" >
      <h1 class="settings__title">
        Organisation info
      </h1>
      <Forbidden />
    </div>
    <div
      v-else-if="!forbidden"
      class="settings--limit-width greedy">

      <h1 class="settings__title">
        Organisation info
      </h1>

      <label
        for="file"
        class="m-0">
        <div id="logo-wrapper">
          <span
            id="logo"
            :style="logoUrl ? `background-image: url(${logoUrl}); background-size: cover; border: 1px solid ${SBColors.greyBorder};` : ''" />
          <DropHere
            :force="true"
            type="basic" />
        </div>
      </label>

      <ValidationObserver
        ref="customFieldObserver"
        v-slot="{ valid, dirty }" >

        <b-form-group>
          <label
            class="flex flex-align-center" >
            Name
          </label>
          <ValidationProvider
            v-slot="{ errors }"
            name="Name"
            rules="required">
            <b-input
              v-model="orgName"
              autocomplete="off"
              class="sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="Enter organisation name" />
            <div
              v-if="errors[0]"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </b-form-group>

        <span
          v-if="errorMessage"
          class="error-message flex">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          {{ errorMessage }}
        </span>

        <FooterActions
          :form-valid="valid && (nameWaitingToBeUpdated || !!(processedMedia && processedMedia.url))"
          :has-changes="dirty || nameWaitingToBeUpdated || !!(processedMedia && processedMedia.url)"
          :show-cancel="nameWaitingToBeUpdated || !!(processedMedia && processedMedia.url)"
          :save-busy="saving"
          @reset="resetChanges"
          @save="updateInfo" />
      </ValidationObserver>
    </div>

    <span class="version">{{ version }}</span>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DropHere from '@/components/common/DropHere';

import { getFileExtension, getMediaType } from '@/helpers/mediaHelpers';
import ToasterMixin from '@/mixins/Toaster';
import NoPermissionMixin from '@/mixins/AccessPermission';
import FooterActions from './partials/FooterActions';
import SBColors from '@/helpers/lists/colors';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'OrgaisationInfoSettings',
  components: {
    FooterActions,
    DropHere,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
    UserPermissionsMixin,
  ],
  data() {
    return {
      errorMessage: '',
      orgName: '',
      orgLogo: '',
      saving: false,
      processedMedia: {},
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      apiError: 'organisation/apiError',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    accessDenied2() {
      return this.forbidden || !this.checkPermission('manage_organisation_settings');
    },
    nameWaitingToBeUpdated() {
      return this.orgName !== this.currentOrganisation.name;
    },
    logoUrl() {
      return !this.resettingData && this.processedMedia.url ? this.processedMedia.url : this.currentOrganisation.logo;
    },
    version() {
      return process.env.VUE_APP_VERSION;
    },
  },
  watch: {
    apiError(error) {
      this.errorMessage = error.data.message;
    },
  },
  mounted() {
    this.finalizeSplashView();
    this.orgName = this.currentOrganisation.name;
    this.orgLogo = this.currentOrganisation.logo;

    this.$eventBus.$on('mediaDrop', this.mediaDropHandler);
    this.$eventBus.$on('dropStats', this.dropStats);
  },
  beforeDestroy() {
    this.$eventBus.$off('mediaDrop', this.mediaDropHandler);
    this.$eventBus.$off('dropStats', this.dropStats);
  },
  methods: {
    ...mapActions({
      finalizeSplashView: 'organisation/finalizeSplashView',
      updateOrganisationInfo: 'organisation/updateOrganisationInfo',
    }),
    resetChanges() {
      this.orgName = this.currentOrganisation.name;
      this.processedMedia = {};
      this.$refs.customFieldObserver.reset();
    },
    updateInfo() {
      const formData = new FormData();
      this.saving = true;

      formData.append('name', this.orgName);

      if (this.processedMedia.file) {
        formData.append('file', this.processedMedia.file);
      }

      const data = {
        organisationId: this.currentOrganisation.id,
        newLogoUrl: this.processedMedia.url,
        data: formData,
      };

      this.updateOrganisationInfo(data)
        .then((response) => {
          if (response.status === 200) {
            this.showToaster('Info has been successfully updated.', null, null, 'OK');
          }
        })
        .catch(() => {
          this.showToaster('Something went wrong. Please try again.', null, null, 'error');
        })
        .finally(() => {
          this.resetChanges();
          this.$nextTick(() => {
            this.saving = false;
          });
        });
    },
    dropStats(stats) {
      if (
        (stats.unsupportedMedia.length > 0) &&
        (stats.supportedMedia.length === 0) &&
        !this.visible
      ) {
        this.showToaster('The file format is not supported.', null, null, 'OK');
      }
    },
    /**
     * Handler for media drop event. Accepts single file or array of files.
     *
     * @param droppedMedia Media file(s) dropped on the messages section.
     */
    mediaDropHandler(droppedMedia) {
      if (Array.isArray(droppedMedia)) {
        this.processMedia(droppedMedia[0]);
      } else {
        this.processMedia(droppedMedia);
      }
    },
    /**
     * Processes media file setting it url, thumbnail for video, caption, type etc.
     *
     * @param media Media to process.
     */
    processMedia(media) {
      let newMedia = {};
      const URLObj = window.URL || window.webkitURL;
      const mediaType = media.file.type ? getMediaType(media.file.type) : getMediaType(getFileExtension(media.name));

      if (mediaType === 'video') {
        return;
      }

      newMedia = {
        id: media.id,
        name: media.name,
        url: URLObj.createObjectURL(media.file),
        thumbnail: null,
        caption: '',
        mediaType,
        file: media.file,
      };

      this.processedMedia = newMedia;
      this.processedMedia = { ...this.processedMedia };
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
}

.settings--limit-width {
  height: unset !important;
}

.version {
  font-size: 12px;
  color: $greyPrimary;
  padding: 10px 0;
  display: block;
  background: $white;
}

#logo-wrapper {
  width: 76px;
  height: 76px;
  margin: 31px 0px 11px;
  padding: 4px;
  border-radius: 5px;
  position: relative;

  &:hover {
    background-color: $linkHover;
    cursor: pointer;
  }

  #logo {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 500px;
    background-color: #deedfe;
    background-image: url('~@/assets/img/SB-Icons/user-business-blue.svg');
    background-size: 35px;
    background-repeat: no-repeat;
    background-position: center;
  }
}
</style>
